import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Divider, Typography } from '@mui/material';
import Button from 'components/V2/Button';
import Input from 'components/V2/Input';
import { useFormik } from 'formik';
import useApiHandler from 'hooks/useApiHandler';
import PropTypes from 'prop-types';

import { openToast } from 'redux/actions/toaster';
import { useSetNewPassword, useSetNewPasswordInvite, useVerifyToken } from 'redux/slices/loginApi';

import classes from '../commonStlyes.module.css';
import styles from './reNewPassword.module.css';

import { ReactComponent as EyeOn } from 'asset/IconsPack/hx_eye-filled.svg';
import { ReactComponent as EyeOff } from 'asset/IconsPack/hx_eye-off-filled.svg';

import {
    reNewPasswordFormInitialValues as initialValues,
    reNewPasswordFormValidationSchema as validationSchema,
} from '../validations';

const ReNewPassword = ({ type }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams();
    const [passwordType, setPasswordType] = useState({
        password: true,
        confirmPassword: true,
    });
    const [tokenData, setTokenData] = useState(null);

    const apiHandler = useApiHandler();
    const [setNewPassword] = useSetNewPassword();
    const [setNewPasswordInvite] = useSetNewPasswordInvite();
    const [verifyToken] = useVerifyToken();

    const throwMessage = (variant, message, redirect = false) => {
        if (redirect) {
            history.push('/');
        }
        dispatch(openToast({ variant, message }));
    };

    const handleApiResponse = (response) => {
        if (response?.data?.success) {
            throwMessage('success', 'Password has changed.', true);
        } else if (response?.error) {
            throwMessage('error', response?.error?.data?.errorMessage, false);
        }
    };

    const handleFormSubmit = async (values) => {
        if (type === 'forgot-password') {
            if (!tokenData) {
                throwMessage('error', 'Token verification failed. Please try again.', true);
                return;
            }
            const response = await apiHandler(setNewPassword, {
                password: values.password,
                passwordConfirm: values.confirmPassword,
                resetToken: tokenData.pw_reset_token,
                uuid: tokenData.uuid,
            });
            handleApiResponse(response);
        } else if (type === 'invite-password') {
            const response = await apiHandler(setNewPasswordInvite, {
                password: values.password,
                confirm_password: values.confirmPassword,
                invite_token: params.token,
            });
            handleApiResponse(response);
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleFormSubmit,
    });

    const handlePasswordTypeChange = (field) => {
        setPasswordType({
            ...passwordType,
            [field]: !passwordType[field],
        });
    };

    useEffect(() => {
        const verifyResetToken = async () => {
            if (type === 'forgot-password' && params?.token) {
                const response = await apiHandler(verifyToken, params.token);

                if (response?.data?.user) {
                    setTokenData(response.data.user);
                } else if (response?.error) {
                    throwMessage(
                        'error',
                        response?.error?.data?.errorMessage || 'Invalid or expired token',
                        true
                    );
                }
            }
        };

        verifyResetToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.token, type]);

    return (
        <Box className={classes.formContainer}>
            <Typography component={'h2'} className={classes.formHeading}>
                Create New Password
            </Typography>
            <Divider className={classes.divider} />
            <form className={classes.form} onSubmit={formik.handleSubmit}>
                <Box>
                    <Input
                        type={passwordType.password ? 'password' : 'text'}
                        variant="outlined"
                        label="Password"
                        name="password"
                        value={formik.values.password}
                        placeholder="Enter password"
                        endAdornment={formik.values.password}
                        EndAdornmentIcon={passwordType.password ? EyeOff : EyeOn}
                        emdAdormentIconClass={styles.eyeIcon}
                        endAdormentClick={() => handlePasswordTypeChange('password')}
                        handleChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        autoFocus
                    />
                </Box>
                <Box>
                    <Input
                        type={passwordType.confirmPassword ? 'password' : 'text'}
                        variant="outlined"
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={formik.values.confirmPassword}
                        placeholder="Enter confirm password"
                        endAdornment={formik.values.confirmPassword}
                        EndAdornmentIcon={passwordType.confirmPassword ? EyeOff : EyeOn}
                        emdAdormentIconClass={styles.eyeIcon}
                        endAdormentClick={() => handlePasswordTypeChange('confirmPassword')}
                        handleChange={formik.handleChange}
                        error={
                            formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)
                        }
                        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    />
                </Box>

                <Button intent="primary" size="lg" type="submit" className={classes.formBtn}>
                    Reset Password
                </Button>
            </form>
        </Box>
    );
};

ReNewPassword.propTypes = {
    type: PropTypes.string,
};

export default ReNewPassword;
